import type { Asset } from '@schibsted-svp/svp-api-types';
import { usePrevious } from 'hooks/usePrevious';
import { isAudio, isLive } from 'models/asset';
import { useEffect } from 'react';
import { useWatch, type Control, type ControllerRenderProps, type UseFormSetValue } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import type { Thumbnail } from 'services/player-api-client/thumbnails';
import { getCategory } from 'store/categories/selectors';
import { fetchThumbnails } from 'store/thumbnails/actions';
import { getAreThumbnailsLoading, getSuggestedThumbnails } from 'store/thumbnails/selectors';
import { revokeVideoPosterPreview } from 'store/video/upload/actions';
import { getUploadedVideoPosterUri } from 'store/video/upload/selectors';
import { getNewsroomConfig } from '../../../../config';
import { ThemeImageType } from '../../../../screens/NewsroomScreen/PlaylistsScreen/types';
import type { FormValues, ImageType } from '../../types';

export const useFetchThumbnails = ({
    asset,
}: {
    asset: Asset;
}): { thumbnails: Thumbnail[]; areThumbnailsLoading: boolean } => {
    const dispatch = useDispatch();

    const { provider, id } = asset;

    const thumbnails = useSelector((state) => getSuggestedThumbnails(state, { provider, id }));
    const areThumbnailsLoading = useSelector((state) => getAreThumbnailsLoading(state, { provider, id }));

    useEffect(() => {
        if (!thumbnails.length && !isAudio(asset) && !isLive(asset)) {
            dispatch(fetchThumbnails(provider, id));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return { thumbnails, areThumbnailsLoading };
};

export const useUploadedPosterChange = ({
    provider,
    id,
    type,
    onChange,
}: {
    provider: string;
    id: number | string;
    type: ImageType | ThemeImageType;
    onChange: ControllerRenderProps['onChange'];
}) => {
    const uploadedPoster = useSelector((state) => getUploadedVideoPosterUri(state, { id, provider, type }));
    const previousUploadedPoster = usePrevious(uploadedPoster);

    useEffect(() => {
        if (uploadedPoster && uploadedPoster !== previousUploadedPoster) {
            onChange(uploadedPoster);
        }
    }, [uploadedPoster, previousUploadedPoster, onChange]);
};

export const useCategoryChange = ({
    asset,
    control,
    imageTypes,
    setValue,
}: {
    asset: Asset;
    control: Control<FormValues>;
    imageTypes: readonly ImageType[];
    setValue: UseFormSetValue<FormValues>;
}) => {
    const dispatch = useDispatch();

    const categoryId = useWatch({ control, name: 'category.id' });
    const prevCategoryId = usePrevious(categoryId);
    const category = useSelector((state) => getCategory(state, { provider: asset.provider, id: categoryId }));

    const newsroomConfig = getNewsroomConfig(asset.provider);

    useEffect(() => {
        if (categoryId !== prevCategoryId && categoryId && prevCategoryId !== undefined && isAudio(asset)) {
            const categoryImage = category?.additional?.image;
            if (categoryImage) {
                imageTypes.forEach((imageType) => {
                    const isImageChangeable = newsroomConfig?.images.onCategoryChange[imageType] ?? false;
                    if (isImageChangeable) {
                        dispatch(revokeVideoPosterPreview(asset.provider, asset.id, imageType));
                        setValue(`images.${imageType}`, categoryImage, { shouldDirty: true });
                    }
                });
            }
        }
    }, [categoryId, prevCategoryId, category, asset, imageTypes, newsroomConfig, dispatch, setValue]);
};
